import { KrCard } from '@/common/card/KrCard';
import { useDashboardContext } from '@/common/dashboard/DashboardContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { far } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

export const AnomalyDetectionCard = ({
  defaultFormValues,
  dataPlugin,
  reqParam = {},
  setFormValues: setFormValuesProp,
}) => {
  const { setFormValues: setFormValuesFromContext } = useDashboardContext();
  const setFormValues = setFormValuesProp ?? setFormValuesFromContext;
  const rsrc = useRsrc();
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(false);

  const driverLabel = (anomaly) => {
    const parts = [];
    if (anomaly.driverDim1Name) {
      const pct1 =
        anomaly.driverDim1CoveragePct != null ? ` (${Math.round(anomaly.driverDim1CoveragePct * 100)}%)` : '';
      parts.push(`${anomaly.driverDim1Name}${pct1}`);
    }
    if (anomaly.driverDim2Name) {
      const pct2 =
        anomaly.driverDim2CoveragePct != null ? ` (${Math.round(anomaly.driverDim2CoveragePct * 100)}%)` : '';
      parts.push(`${anomaly.driverDim2Name}${pct2}`);
    }
    return parts.length > 0 ? `Drivers: ${parts.join(' · ')}` : null;
  };

  useEffect(() => {
    if (!dataPlugin || !reqParam) {
      return;
    }

    setLoading(true);
    rsrc.data('sc', dataPlugin, reqParam).then((data) => {
      setAnomalies(data ?? []);
      setLoading(false);
    });
  }, [dataPlugin, JSON.stringify(reqParam)]);

  const handleRowClick = (anomaly) => {
    setFormValues({
      ...defaultFormValues,
      ...anomaly.formValues,
    });
  };

  return (
    <KrCard title="Anomaly Detection" icon={far.faSparkles} variant="smart">
      {loading ?
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            py: 4,
          }}
        >
          <CircularProgress size={28} />
          <Typography variant="caption">Calculating Anomalies...</Typography>
        </Box>
      : <Stack spacing={0} divider={<Divider sx={{ my: 0.5 }} />}>
          {anomalies.map((anomaly, index) => {
            const dim2Name = anomaly.dim2Name ? ` + ${anomaly.dim2Name}` : '';
            const drivers = driverLabel(anomaly);
            return (
              <Box key={anomaly.id}>
                <Box
                  onClick={() => handleRowClick(anomaly)}
                  sx={{
                    cursor: 'pointer',
                    px: 1,
                    py: 1,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 20,
                      height: 20,
                      borderRadius: 999,
                      backgroundColor: 'action.selected',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {anomaly.dim1Name}
                      {dim2Name}
                    </Typography>
                    <Typography variant="caption" sx={{ lineHeight: 1.3 }}>
                      {anomaly.reasonText}
                    </Typography>
                    {drivers && (
                      <Typography variant="caption" sx={{ lineHeight: 1.3, display: 'block' }}>
                        {drivers}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Stack>
      }
    </KrCard>
  );
};
